import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import api, { getTransactions, getFieldsByVenue } from '../services/api';
import Loading from '../components/Common/Loading';
import Pagination from '../components/Common/Pagination';
import AdminManagement from '../components/Admin/AdminManagement';
import DeviceManagement from '../components/Admin/DeviceManagement';
import ProductManagement from '../components/Admin/ProductManagement';
import ProductDevManagement from '../components/Admin/ProductDevManagement';
import VenueManagement from '../components/Admin/VenueManagement';
import CMSManagement from '../components/Admin/CMSManagement';
import ErrorsManagement from '../components/Admin/ErrorsManagement';
import StorageQuotaManagement from '../components/Admin/StorageQuotaManagement';
import { useLocalVideos } from '../hooks/useLocalVideos';
import { uploadVideosToGoogleDrive } from '../utils/zipUtils';
import * as XLSX from 'xlsx';
import '../styles/AdminPage.css';

interface Transaction {
  id: string;
  email: string;
  videoCount: number;
  videoTitles?: string[]; // Add videoTitles property
  videoIds?: string[]; // Add videoIds property
  totalAmount: number;
  createdAt: string;
  paymentStatus: string;
  emailSent?: string;
  venueName?: string;
  venueId?: number | null;
  fieldName?: string;
  fieldId?: number | null;
  zipUrl?: string;
  promoCode?: string | null;
}

interface Venue {
  id: string;
  name: string;
}

// Define tab types
enum AdminTab {
  TRANSACTIONS = 'transactions',
  ADMINS = 'admins',
  DEVICES = 'devices',
  PRODUCT_MANAGEMENT = 'productManagement',
  PRODUCT_DEV_MANAGEMENT = 'productDevManagement',
  VENUE_MANAGEMENT = 'venueManagement',
  CMS_MANAGEMENT = 'cmsManagement',
  ERRORS = 'errors',
  STORAGE_QUOTA = 'storageQuota'
}

const TRANSACTIONS_PER_PAGE = 25;

const AdminPage: React.FC = () => {
  const { admin, logoutAdmin, isSuperAdmin } = useAdminAuth();
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<AdminTab>(AdminTab.TRANSACTIONS);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [fields, setFields] = useState<{ id: string; name: string }[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [searchEmail, setSearchEmail] = useState<string>('');
  const [selectedField, setSelectedField] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [resendingEmailIds, setResendingEmailIds] = useState<string[]>([]);

  // Add a state for export loading
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get local videos hook functions
  const { 
    selectClipoFolder, 
    getVideoFiles,
    hasSelectedFolder 
  } = useLocalVideos();

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const adminUser = localStorage.getItem('adminUser');
      if (!adminUser) {
        setError('You are not logged in. Please log in again.');
        setLoading(false);
        return;
      }
      const filters: any = {
        page: currentPage,
        limit: TRANSACTIONS_PER_PAGE
      };
      if (selectedVenue !== 'all') filters.venueId = selectedVenue;
      if (selectedField !== 'all') filters.fieldId = selectedField;
      if (searchEmail) filters.email = searchEmail;
      if (startDate) filters.startDate = startDate.toISOString();
      if (endDate) filters.endDate = endDate.toISOString();
      
      const response = await getTransactions(filters);
      if (response.transactions) {
        const transformedTransactions = response.transactions.map((transaction: any) => ({
          id: transaction.id,
          email: transaction.email,
          videoCount: transaction.videoCount,
          videoTitles: transaction.videoTitles || [],
          videoIds: transaction.videoIds || [],
          totalAmount: transaction.totalAmount,
          createdAt: transaction.createdAt,
          paymentStatus: transaction.paymentStatus || 'pending',
          emailSent: transaction.emailSent || null,
          venueName: transaction.venueName || 'Unknown Venue',
          venueId: transaction.venueId || null,
          fieldName: transaction.fieldName || null,
          fieldId: transaction.fieldId || null,
          zipUrl: transaction.zipUrl || null,
          promoCode: transaction.promoCode || null
        }));
        setTransactions(transformedTransactions);
        setCurrentPage(response.currentPage || 1);
        setTotalPages(response.totalPages || 1);
      } else {
        setTransactions([]);
        setCurrentPage(1);
        setTotalPages(1);
      }
    } catch (err: any) {
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch fields when venue changes
  const fetchFields = async () => {
    if (selectedVenue === 'all') {
      setFields([]);
      return;
    }
    
    try {
      const fieldsData = await getFieldsByVenue(selectedVenue);
      setFields(fieldsData);
    } catch (err) {
      console.error('Failed to fetch fields:', err);
      setFields([]);
    }
  };

  // Add a separate effect to fetch venues on mount for all admin types
  useEffect(() => {
    fetchVenues();
  }, []);

  // Fetch transactions when filters or page changes
  useEffect(() => {
    fetchTransactions();
  }, [selectedVenue, selectedField, searchEmail, startDate, endDate, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedVenue, selectedField, searchEmail, startDate, endDate]);

  // Add effect to fetch fields when venue changes
  useEffect(() => {
    if (selectedVenue !== 'all') {
      fetchFields();
    } else {
      setFields([]);
      setSelectedField('all');
    }
  }, [selectedVenue]);

  // Check for tab query parameter when component mounts
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tabParam = query.get('tab');
    
    if (tabParam === 'admins' && isSuperAdmin) {
      setActiveTab(AdminTab.ADMINS);
    } else if (tabParam === 'devices') {
      setActiveTab(AdminTab.DEVICES);
    } else if (tabParam === 'venues' && isSuperAdmin) {
      setActiveTab(AdminTab.VENUE_MANAGEMENT);
    }
  }, [location.search, isSuperAdmin]);

  const fetchVenues = async () => {
    try {
      const adminUser = localStorage.getItem('adminUser');
      if (!adminUser) {
        console.error('No admin user found in localStorage');
        return;
      }
      
      const response = await api.get('/admin/venues');
      setVenues(response.data);
    } catch (err: any) {
      console.error('Failed to fetch venues:', err);
    }
  };

  const handleVenueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVenue(e.target.value);
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedField(e.target.value);
  };

  const handleLogout = () => {
    logoutAdmin();
    history.push('/admin/login');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setSearchEmail('');
    setStartDate(null);
    setEndDate(null);
    if (isSuperAdmin) {
      setSelectedVenue('all');
    }
    setSelectedField('all');
  };

  const handleResendEmail = async (id: string) => {
    try {
      // Clear any previous error and success message
      setError(null);
      setSuccessMessage(null);
      
      setResendingEmailIds([...resendingEmailIds, id]);
      const response = await api.post(`/transaction/${id}/resend-email`);
      
      // Check if we need to search locally
      if (response.data.searchLocally) {
        // Get the transaction info from the response
        const transactionInfo = response.data.transaction;
        
        // Ask user to select the folder containing videos
        const folderResult = await selectClipoFolder(true);
        
        if (folderResult.success && hasSelectedFolder) {
          // Get the actual file objects using the videoIds from the transaction
          const videoIds = transactionInfo.videoIds || [];
          
          if (videoIds.length === 0) {
            setError('No video information found for this transaction');
            // Set transaction status to failed
            setTransactions(prevTransactions => 
              prevTransactions.map(t => 
                t.id === id
                  ? { ...t, emailSent: 'failed' }
                  : t
              )
            );
            return;
          }
          
          try {
            // Find the video files
            const videoFiles = await getVideoFiles(videoIds);
            
            if (videoFiles.length === 0) {
              setError('No matching videos found in the selected folder');
              // Set transaction status to failed
              setTransactions(prevTransactions => 
                prevTransactions.map(t => 
                  t.id === id
                    ? { ...t, emailSent: 'failed' }
                    : t
                )
              );
              return;
            }
            
            // Generate folder name with date and time
            const now = new Date();
            const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
            const folderName = `videos_${dateStr}_${timeStr}_${videoFiles.length}videos`;
            
            // Upload videos to Google Drive
            const zipUrl = await uploadVideosToGoogleDrive(videoFiles, folderName);
            
            if (!zipUrl) {
              setError('Failed to upload videos. Please try again.');
              // Mark transaction as failed if upload fails
              await api.updateTransactionZipUrl(parseInt(id), null, true);
              setTransactions(prevTransactions => 
                prevTransactions.map(t => 
                  t.id === id
                    ? { ...t, emailSent: 'failed' }
                    : t
                )
              );
              return;
            }
            
            // Update transaction with zipUrl
            await api.updateTransactionZipUrl(parseInt(id), zipUrl);
            
            // Now that we have a zipUrl, resend the email
            const resendResponse = await api.post(`/transaction/${id}/resend-email`);
            
            // Update transaction status in the list
            setTransactions(prevTransactions => 
              prevTransactions.map(t => 
                t.id === id
                  ? { ...t, zipUrl, emailSent: 'sent' }
                  : t
              )
            );
            
            // Show temporary success message
            setSuccessMessage('Videos found, uploaded, and email sent successfully');
            setTimeout(() => setSuccessMessage(null), 5000);
          } catch (err) {
            console.error('Error processing videos:', err);
            setError('Error processing videos. Please try again.');
            // Set transaction status to failed
            setTransactions(prevTransactions => 
              prevTransactions.map(t => 
                t.id === id
                  ? { ...t, emailSent: 'failed' }
                  : t
              )
            );
          }
        } else {
          setError('No stored folder found. Please use the Video Selection page first to select a folder.');
          // Set transaction status to failed
          setTransactions(prevTransactions => 
            prevTransactions.map(t => 
              t.id === id
                ? { ...t, emailSent: 'failed' }
                : t
            )
          );
        }
      } else if (response.status === 200) {
        // Update the transaction in the current list without refetching
        setTransactions(prevTransactions => 
          prevTransactions.map(t => 
            t.id === id
              ? { ...t, emailSent: 'sent' }
              : t
          )
        );
        
        // Show temporary success message
        setSuccessMessage('Email resent successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      console.error('Failed to resend email:', err);
      
      // Get a user-friendly error message
      let errorMessage = 'Failed to resend email';
      
      if (err.response) {
        console.error('Error response:', err.response.status, err.response.data);
        if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Set transaction status to failed
      setTransactions(prevTransactions => 
        prevTransactions.map(t => 
          t.id === id
            ? { ...t, emailSent: 'failed' }
            : t
        )
      );
      
      setError(errorMessage);
    } finally {
      setResendingEmailIds(resendingEmailIds.filter(i => i !== id));
    }
  };

  const handleDiagnostics = async () => {
    // This function is no longer used, but keeping it for now as it might be re-introduced later.
    // setDiagnosisRunning(true);
    // try {
    //   await runCorsDiagnostics();
    // } catch (err) {
    //   console.error('Error running diagnostics:', err);
    // } finally {
    //   setDiagnosisRunning(false);
    // }
  };

  // Function to fetch all transactions for export
  const fetchAllTransactionsForExport = async () => {
    try {
      setExportLoading(true);
      const adminUser = localStorage.getItem('adminUser');
      if (!adminUser) {
        setError('You are not logged in. Please log in again.');
        setExportLoading(false);
        return null;
      }
      
      // Create filter object with all current filters except pagination
      const filters: any = {
        // Set a very high limit to ensure we get all transactions in one request
        limit: 10000
      };
      
      if (selectedVenue !== 'all') filters.venueId = selectedVenue;
      if (selectedField !== 'all') filters.fieldId = selectedField;
      if (searchEmail) filters.email = searchEmail;
      if (startDate) filters.startDate = startDate.toISOString();
      if (endDate) filters.endDate = endDate.toISOString();
      
      console.log('Fetching all transactions for export with filters:', filters);
      
      const response = await getTransactions(filters);
      
      if (response.transactions) {
        return response.transactions.map((transaction: any) => ({
          id: transaction.id,
          email: transaction.email,
          videoCount: transaction.videoCount,
          videoTitles: transaction.videoTitles || [],
          videoIds: transaction.videoIds || [],
          totalAmount: transaction.totalAmount,
          createdAt: transaction.createdAt,
          paymentStatus: transaction.paymentStatus || 'pending',
          emailSent: transaction.emailSent || null,
          venueName: transaction.venueName || 'Unknown Venue',
          venueId: transaction.venueId || null,
          fieldName: transaction.fieldName || null,
          fieldId: transaction.fieldId || null,
          zipUrl: transaction.zipUrl || null,
          promoCode: transaction.promoCode || null
        }));
      }
      
      return null;
    } catch (err: any) {
      setError('Failed to fetch transactions for export. Please try again.');
      return null;
    } finally {
      setExportLoading(false);
    }
  };

  // Update the export function to use all transactions
  const exportTransactionsToExcel = async () => {
    try {
      // Show loading state
      setExportLoading(true);
      setError(null);
      
      // Fetch all transactions with current filters
      const allTransactions = await fetchAllTransactionsForExport();
      
      if (!allTransactions || allTransactions.length === 0) {
        setError('No transactions to export.');
        setExportLoading(false);
        return;
      }
      
      console.log(`Exporting ${allTransactions.length} transactions to Excel`);
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Format the transaction data for Excel
      const excelData = allTransactions.map((transaction: Transaction) => ({
        ID: transaction.id,
        Email: transaction.email,
        'Video Count': transaction.videoCount,
        'Video Titles': transaction.videoTitles ? transaction.videoTitles.join(', ') : '',
        Amount: `Rp ${transaction.totalAmount.toLocaleString('id-ID')}`,
        'Payment Status': transaction.paymentStatus,
        'Email Status': transaction.emailSent || 'Not set',
        Date: new Date(transaction.createdAt).toLocaleString(),
        Venue: transaction.venueName || 'Unknown Venue',
        Court: transaction.fieldName || 'No Field',
        'Promo Code': transaction.promoCode || '-',
        'ZIP URL': transaction.zipUrl || '-'
      }));

      // Create a worksheet from the data
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

      // Generate Excel file and trigger download
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      const fileName = `clipo_transactions_${dateStr}.xlsx`;
      
      // Trigger download
      XLSX.writeFile(wb, fileName);
      
      // Show temporary success message with positive styling
      setSuccessMessage(`Successfully exported ${allTransactions.length} transactions to Excel`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error exporting transactions:', err);
      setError('Failed to export transactions. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Render component based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case AdminTab.TRANSACTIONS:
        return (
          <div className="transactions-section">
            <div className="admin-filters">
              <h2>Transaction Filters</h2>
              
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="emailSearch">Search by Email</label>
                  <input
                    type="text"
                    id="emailSearch"
                    className="admin-search-input"
                    placeholder="Enter email..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="startDate">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    className="admin-date-picker"
                    value={startDate ? startDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="endDate">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    className="admin-date-picker"
                    value={endDate ? endDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                    max={new Date().toISOString().split('T')[0]}
                    min={startDate ? startDate.toISOString().split('T')[0] : ''}
                  />
                </div>
              </div>
              
              <div className="reset-button-row">
                <button
                  className="admin-reset-button"
                  onClick={resetFilters}
                >
                  Reset Filters
                </button>
                
                {/* Update export button to show when there are transactions or filters applied */}
                {(transactions.length > 0 || searchEmail || startDate || endDate || selectedVenue !== 'all' || selectedField !== 'all') && (
                  <button
                    className="admin-export-button"
                    onClick={exportTransactionsToExcel}
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <>
                        <div className="button-spinner"></div>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {loading && transactions.length === 0 ? (
              <Loading />
            ) : error ? (
              <div className="admin-error">
                <p>{error}</p>
              </div>
            ) : successMessage ? (
              <div className="admin-success">
                <p>{successMessage}</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="no-transactions">
                <p>No transactions found. Try adjusting your filters.</p>
                <button
                  className="admin-reset-button"
                  onClick={resetFilters}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <>
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Email</th>
                        <th>Videos</th>
                        <th>Video Titles</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Payment Status</th>
                        <th>Email Status</th>
                        {isSuperAdmin && <th>Venue</th>}
                        <th>Court</th>
                        <th>Promo Code</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(transaction => (
                        <tr key={transaction.id}>
                          <td className="id-cell">{transaction.id}</td>
                          <td className="email-cell truncated" data-full-text={transaction.email}>{transaction.email}</td>
                          <td>{transaction.videoCount}</td>
                          <td className="video-titles-cell truncated" data-full-text={transaction.videoTitles?.join(', ')}>
                            {transaction.videoTitles && transaction.videoTitles.length > 0 
                              ? transaction.videoTitles.join(', ').substring(0, 30) + (transaction.videoTitles.join(', ').length > 30 ? '...' : '')
                              : '-'}
                          </td>
                          <td className="amount-cell">Rp {transaction.totalAmount.toLocaleString('id-ID')}</td>
                          <td className="date-cell">
                            {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false})}
                          </td>
                          <td>
                            <span className={`status-badge status-${transaction.paymentStatus.toLowerCase()}`}>
                              {transaction.paymentStatus}
                            </span>
                          </td>
                          <td>
                            {transaction.emailSent ? (
                              <span className={`status-badge email-status-${transaction.emailSent.toLowerCase()}`}>
                                {transaction.emailSent}
                              </span>
                            ) : (
                              <span className="status-badge email-status-null">
                                Not set
                              </span>
                            )}
                          </td>
                          {isSuperAdmin && <td>{transaction.venueName}</td>}
                          <td>{transaction.fieldName || 'No Field'}</td>
                          <td>{transaction.promoCode || '-'}</td>
                          <td>
                            <button
                              className="resend-button"
                              onClick={() => handleResendEmail(transaction.id)}
                              disabled={
                                resendingEmailIds.includes(transaction.id)
                              }
                            >
                              {resendingEmailIds.includes(transaction.id) ? 'Sending...' : 'Resend'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="admin-pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </div>
        );
      case AdminTab.ADMINS:
        return <AdminManagement />;
      case AdminTab.DEVICES:
        return <DeviceManagement venues={venues} />;
      case AdminTab.PRODUCT_MANAGEMENT:
        return <ProductManagement />;
      case AdminTab.PRODUCT_DEV_MANAGEMENT:
        return <ProductDevManagement />;
      case AdminTab.VENUE_MANAGEMENT:
        return <VenueManagement />;
      case AdminTab.CMS_MANAGEMENT:
        return <CMSManagement />;
      case AdminTab.ERRORS:
        return <ErrorsManagement />;
      case AdminTab.STORAGE_QUOTA:
        return <StorageQuotaManagement />;
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-title">
          <h1>Admin Dashboard</h1>
          <p>Welcome, {admin?.email}</p>
        </div>
        
        <div className="admin-actions">
          <button 
            className="logout-button" 
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === AdminTab.TRANSACTIONS ? 'active' : ''}`}
          onClick={() => setActiveTab(AdminTab.TRANSACTIONS)}
        >
          Transactions
        </button>
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.ADMINS ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.ADMINS)}
          >
            Admins
          </button>
        )}
        
        <button 
          className={`admin-tab ${activeTab === AdminTab.DEVICES ? 'active' : ''}`}
          onClick={() => setActiveTab(AdminTab.DEVICES)}
        >
          Devices
        </button>
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.PRODUCT_MANAGEMENT ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.PRODUCT_MANAGEMENT)}
          >
            Product
          </button>
        )}
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.PRODUCT_DEV_MANAGEMENT ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.PRODUCT_DEV_MANAGEMENT)}
          >
            Product - Staging
          </button>
        )}
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.VENUE_MANAGEMENT ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.VENUE_MANAGEMENT)}
          >
            Venues
          </button>
        )}
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.CMS_MANAGEMENT ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.CMS_MANAGEMENT)}
          >
            CMS
          </button>
        )}
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.ERRORS ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.ERRORS)}
          >
            Errors
          </button>
        )}
        
        {isSuperAdmin && (
          <button 
            className={`admin-tab ${activeTab === AdminTab.STORAGE_QUOTA ? 'active' : ''}`}
            onClick={() => setActiveTab(AdminTab.STORAGE_QUOTA)}
          >
            Storage Quota
          </button>
        )}
        
      </div>
      
      <div className="admin-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminPage;